// "Close" in the open ticket's header: closes the ticket in the helpdesk and in Kim without sending
// anything. The ticket stays open on screen and is refreshed, and the lists are refreshed so it
// moves under Closed.

import { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from '@/components/ui/sonner';
import { invalidateQueries } from '@/lib/query-client';
import { sentinelWorkspaceService } from '@/services/sentinelWorkspace.service';

export interface CloseTicket {
  close: (ticketId: number) => Promise<void>;
  /** The ticket being closed right now, so its button can show progress. */
  pendingTicketId: number | null;
}

function refreshLists(): void {
  void invalidateQueries(['sentinel-workspace', 'tickets']);
  void invalidateQueries(['sentinel', 'unassigned-counts']);
}

function failureMessage(error: unknown): string {
  if (!axios.isAxiosError(error)) return 'Could not close the ticket. Please try again.';
  const status = error.response?.status;
  if (status === 404) return 'This ticket no longer exists';
  if (status === 409) return 'This ticket cannot be closed right now. Try again in a moment.';
  if (status === 502) return 'The helpdesk did not close the ticket. Check it in the helpdesk.';
  return 'Could not close the ticket. Please try again.';
}

export function useCloseTicket(refreshTicket: () => void): CloseTicket {
  const [pendingTicketId, setPendingTicketId] = useState<number | null>(null);

  const close = useCallback(
    async (ticketId: number) => {
      setPendingTicketId(ticketId);
      try {
        await sentinelWorkspaceService.closeTicket(ticketId);
        toast.success('Ticket closed');
        refreshTicket();
      } catch (error) {
        toast.error(failureMessage(error));
      } finally {
        refreshLists();
        setPendingTicketId(null);
      }
    },
    [refreshTicket],
  );

  return { close, pendingTicketId };
}
